import React from "react";
import { LifeBuoy, Check, Camera, ArrowRight } from "lucide-react";

const REASON_LABEL = {
  blurry: "This copy is hard to read",
  incomplete: "This looks like it may be missing something",
  missing_signature: "A signature looks like it may be missing",
  missing_seal: "The official seal looks like it may be missing",
  conflicts_memory: "Something doesn't match what Dox knows about your business",
  blank_fields: "Some required fields look blank",
  expired: "This document appears to be expired",
  unsupported_version: "This looks like an unsupported version",
  other: "Dox wants to double-check this one",
  none: "Dox wants to double-check this one",
};

/**
 * Supportive coaching panel. Dox never rejects or accuses — it helps the owner
 * get a clean, verifiable document and stays with them until it's resolved.
 */
export default function CoachPanel({ analysis, onRetake, compact = false }) {
  if (!analysis) return null;
  const reason = analysis.attention_reason || "other";
  const title = analysis.readable === false ? "Dox needs a clearer copy" : REASON_LABEL[reason] || REASON_LABEL.other;

  return (
    <div
      data-testid="coach-panel"
      className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.06] p-5 sm:p-6"
    >
      <div className="flex items-start gap-3">
        <span className="h-10 w-10 rounded-xl bg-amber-400/15 text-amber-400 flex items-center justify-center shrink-0">
          <LifeBuoy size={20} />
        </span>
        <div className="min-w-0">
          <h3 className="font-display font-bold text-white text-lg leading-tight">{title}</h3>
          <p className="text-white/70 text-sm leading-relaxed mt-2" data-testid="coach-message">
            {analysis.coach_message ||
              "I couldn't fully process this one yet. Let's get a cleaner copy so I can help you properly."}
          </p>
        </div>
      </div>

      {analysis.coach_suggestions?.length > 0 && (
        <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
          {analysis.coach_suggestions.map((s, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <span className="mt-0.5 h-5 w-5 rounded-full bg-amber-400/15 text-amber-400 flex items-center justify-center shrink-0">
                <Check size={12} />
              </span>
              <span className="text-sm text-white/75">{s}</span>
            </div>
          ))}
        </div>
      )}

      {analysis.recommended_action && (
        <div className="mt-5 flex items-center gap-2 text-sm">
          <ArrowRight size={15} className="text-dox-orange shrink-0" />
          <span className="text-white/80"><span className="text-white/50">Next best step: </span>{analysis.recommended_action}</span>
        </div>
      )}

      <div className={`mt-6 flex flex-col sm:flex-row ${compact ? "" : "items-center"} gap-3`}>
        {analysis.allow_retake !== false && onRetake && (
          <button
            data-testid="coach-retake-btn"
            onClick={onRetake}
            className="rounded-full bg-dox-orange px-6 py-3 text-sm font-bold text-white flex items-center justify-center gap-2 hover:bg-dox-orangeHover transition-colors"
          >
            <Camera size={16} /> Give Dox a clearer copy
          </button>
        )}
        <span className="text-xs text-white/45">Don't worry — Dox will stay on this with you until it's sorted.</span>
      </div>
    </div>
  );
}
